'use client';

import { useState } from 'react';
import { Button, ListItem } from '@1debit/chimekit';
import { cn } from 'utils/styles';

export interface TocItem {
  label: string;
  anchor: string;
}

/**
 * Scrolls smoothly to an element by its ID with a fixed header offset
 */
const scrollToAnchor = (anchor: string, yOffset = -80): void => {
  const element = document.getElementById(anchor);
  if (element) {
    const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
    window.history.pushState({}, '', `#${anchor}`);
  }
};

export interface TableOfContentsProps {
  /** TOC items to display */
  items: TocItem[];
  /** Title displayed on the toggle button */
  title?: string;
  /** Whether the TOC starts expanded */
  defaultExpanded?: boolean;
  /** Whether the toggle button is disabled */
  disabled?: boolean;
  /** Whether to show numbers before each item */
  showNumbers?: boolean;
  /** Test ID for the toggle button */
  testId?: string;
  /** Additional class names for the container */
  className?: string;
  /** Callback when toggle button is clicked */
  onToggle?: (isOpen: boolean) => void;
  /** Callback when an item is clicked or activated via keyboard */
  onItemClick?: (item: TocItem, e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
}

/**
 * Base TableOfContents component - a collapsible list of anchor links.
 * Use this as a building block for page-specific TOC implementations.
 */
const TableOfContents = ({
  items,
  title = 'Table of Contents',
  defaultExpanded = false,
  disabled = false,
  showNumbers = false,
  testId = 'tableOfContentsTitle',
  className,
  onToggle,
  onItemClick,
}: TableOfContentsProps) => {
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    onToggle?.(newIsOpen);
  };

  const handleItemClick = (item: TocItem, e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    onItemClick?.(item, e);
    scrollToAnchor(item.anchor);
  };

  const handleItemKeyDown = (item: TocItem, e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleItemClick(item, e);
    }
  };

  return (
    <div className={cn('px-2 py-2 mx-auto rounded-md content-wrapper bg-bg-raised-secondary', className)}>
      <Button
        data-testid={testId}
        className="flex items-center justify-between w-full px-4 py-2 font-sans font-semibold rounded-sm text-md active:scale-100"
        analyticsId={null}
        variant="tertiary"
        iconProps={{ icon: isOpen ? 'remove_circle' : 'add_circle', position: 'trailing' }}
        onClick={handleToggle}
        disabled={disabled}>
        {title}
      </Button>
      {isOpen &&
        (showNumbers ? (
          <ol className="px-4 list-decimal list-inside">
            {items.map((item, index) => (
              <li
                key={item.anchor}
                role="button"
                tabIndex={0}
                className={cn(
                  'font-sans font-semibold text-md py-2 hover:underline cursor-pointer focus:outline-none focus:underline',
                  index !== items.length - 1 && 'border-b border-border-primary',
                )}
                onClick={e => handleItemClick(item, e)}
                onKeyDown={e => handleItemKeyDown(item, e)}>
                {item.label}
              </li>
            ))}
          </ol>
        ) : (
          <div className="px-4">
            {items.map((item, index) => (
              <ListItem
                key={item.anchor}
                tag="button"
                analyticsId={item.label}
                onClick={e => handleItemClick(item, e)}
                className={cn(
                  'px-0 py-0 hover:underline rounded-none hover:rounded-none active:scale-100 hover:cursor-pointer',
                  index !== items.length - 1 && 'border-b border-border-primary',
                )}>
                <ListItem.ListContent title={item.label} className="font-sans font-semibold text-md" />
              </ListItem>
            ))}
          </div>
        ))}
    </div>
  );
};

export default TableOfContents;
